/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/shared/validateA1Form.ts.
 */

import type {
  FormDataResponse,
  FormField,
  FormSubSection,
} from "../../../../../interfaces/formInterfaces";
import type { FuelDensityApiRow } from "../../../../../services/a1Service";
import type { CalorificValuePublic } from "../../../../../services/calorificValuesPublicService";
import { A1_RAW_MATERIAL_ROW_FIELD_NAMES } from "../A1/hooks/useA1Prefill";
import {
  type ContributorFormErrors,
  getFirstFormError,
} from "../shared/contributorFormValidation";
import { validateContributorSectionRows } from "../shared/validateContributorSectionRows";
import {
  A1_FUEL_MIX_FIELD,
  A1_FUEL_SECTION_CODE,
  shouldSkipCalorificValueMandatoryValidation,
  shouldSkipFuelDensityMandatoryValidation,
} from "./fuelMixDensityValidation";

export { getFirstFormError };

const A1_FORCE_EDITABLE = new Set([
  "characterizationFactor_unit_extraction",
  "embodiedCarbon_referenceQty_unit_extraction",
  "embodiedCarbon_functionalUnit_unit_extraction",
]);

function isA1FieldDisabled(field: FormField): boolean {
  return (
    (field.type === "READONLY" ||
      field.isEditable === false ||
      field.name === "extractionState" ||
      field.name === "extractionDistrict") &&
    !A1_FORCE_EDITABLE.has(field.name)
  );
}

function shouldSkipA1Field(field: FormField): boolean {
  if (!field.isEnabled) return true;
  if (field.type === "RAW_MATERIALS") return true;
  if (A1_RAW_MATERIAL_ROW_FIELD_NAMES.has(field.name)) return true;
  if (isA1FieldDisabled(field)) return true;
  return false;
}

/** Exported function — validate A1Form. */
export function validateA1Form({
  sections,
  sectionRows,
  values,
  formData = null,
  fuelDensityCatalog = [],
  calorificValuesCatalog = [],
}: {
  sections: FormSubSection[];
  sectionRows: Record<string, number>;
  values: Record<string, string>;
  formData?: FormDataResponse | null;
  fuelDensityCatalog?: FuelDensityApiRow[];
  calorificValuesCatalog?: CalorificValuePublic[];
}): ContributorFormErrors {
  return validateContributorSectionRows({
    sections,
    sectionRows,
    values,
    shouldSkipField: (field, sectionCode, rowIndex) => {
      if (shouldSkipA1Field(field)) return true;
      if (
        shouldSkipFuelDensityMandatoryValidation({
          field,
          sectionCode,
          rowIndex,
          values,
          formData,
          fuelDensityCatalog,
          fuelMixFieldName: A1_FUEL_MIX_FIELD,
          fuelMixSectionCode: A1_FUEL_SECTION_CODE,
        })
      ) {
        return true;
      }
      return shouldSkipCalorificValueMandatoryValidation({
        field,
        sectionCode,
        rowIndex,
        values,
        formData,
        calorificValuesCatalog,
        fuelMixFieldName: A1_FUEL_MIX_FIELD,
        fuelMixSectionCode: A1_FUEL_SECTION_CODE,
      });
    },
  });
}
